import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { EntitlementKey } from '../entitlements/entitlement-keys';
import { EntitlementsService } from '../Services/entitlements.service';
import { TokenEncryptionService } from '../Services/token-encryption.service';

export type EntitlementGuardMode = 'all' | 'any';

/**
 * Route guard: runs after planExpiredGuard.
 * data.requiredEntitlements: string | string[] (company-scoped keys)
 * data.entitlementGuardMode?: 'all' | 'any' (default 'all')
 */
@Injectable({ providedIn: 'root' })
export class EntitlementGuard implements CanActivate {
  constructor(
    private entitlements: EntitlementsService,
    private tokenEncryptionService: TokenEncryptionService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    const user = this.tokenEncryptionService.getEncryptedItem('user');
    this.entitlements.syncFromUser(user);

    if (!this.entitlements.isEnforcementActive()) {
      return true;
    }

    const keys = this.normalizeKeys(route.data['requiredEntitlements']);
    if (!keys.length) {
      return true;
    }

    const mode: EntitlementGuardMode = route.data['entitlementGuardMode'] ?? 'all';
    const allowed =
      mode === 'any'
        ? this.entitlements.hasAnyCompany(keys)
        : this.entitlements.hasAllCompany(keys);

    if (allowed) {
      return true;
    }

    return this.router.createUrlTree(['/feature-locked'], {
      queryParams: {
        keys: keys.join(','),
        returnUrl: state.url,
      },
    });
  }

  private normalizeKeys(raw: unknown): string[] {
    if (!raw) {
      return [];
    }
    if (Array.isArray(raw)) {
      return raw.map(String);
    }
    return [String(raw)];
  }
}

/** Route data presets for pilot gates (SUSEE-1316). */
export const ENTITLEMENT_ROUTE_DATA = {
  productivityTrends: {
    requiredEntitlements: [
      EntitlementKey.productivity_trends,
      EntitlementKey.productivity_trends_preview,
    ],
    entitlementGuardMode: 'any' as EntitlementGuardMode,
  },
  websiteUsage: {
    requiredEntitlements: [EntitlementKey.website_usage],
    entitlementGuardMode: 'all' as EntitlementGuardMode,
  },
  liveWall: {
    requiredEntitlements: [EntitlementKey.live_wall],
    entitlementGuardMode: 'all' as EntitlementGuardMode,
  },
  /** Basic: single screencast; Pro+: live_wall for grid/wall/patrol too. */
  liveMonitoring: {
    requiredEntitlements: [EntitlementKey.live_screencast, EntitlementKey.live_wall],
    entitlementGuardMode: 'any' as EntitlementGuardMode,
  },
  workReplay: {
    requiredEntitlements: [
      EntitlementKey.work_replay,
      EntitlementKey.work_replay_preview,
    ],
    entitlementGuardMode: 'any' as EntitlementGuardMode,
  },
  dataManagement: {
    requiredEntitlements: [
      EntitlementKey.data_management,
      EntitlementKey.data_management_preview,
    ],
    entitlementGuardMode: 'any' as EntitlementGuardMode,
  },
  deviceManagement: {
    requiredEntitlements: [EntitlementKey.device_management],
    entitlementGuardMode: 'all' as EntitlementGuardMode,
  },
  /** Screencast session audit (Professional+ live monitoring). */
  auditLogs: {
    requiredEntitlements: [EntitlementKey.live_wall],
    entitlementGuardMode: 'all' as EntitlementGuardMode,
  },
};
